import React, { useContext, useMemo } from "react";
import { Pagination } from "@components/general/Pagination/Pagination";
import { StickyTableFooter } from "../../general/StickyTableFooter/StickyTableFooter";
import { TransactionsActionBar } from "../TransactionsBulkActions/TransactionsActionBar/TransactionsActionBar";
import { TransactionsTotals } from "../TransactionsTotals";
import { useBulkActions } from "../TransactionsBulkActions/useBulkActions";
import { BulkUpdateStatus } from "../TransactionsBulkActions/lib";
import { PaginatedTransactionsTableContext } from "../PaginatedTransactionsTableContextProvider";
import { useTransactionsStatisticsQuery } from "../useTransactionsStatisticsQuery";
import stickyFooterStyles from "../../general/StickyTableFooter/StickyTableFooter.module.scss";

interface Props {
    sticky?: boolean;
}

export const TransactionsViewFooter: React.FC<Props> = ({ sticky = true }) => {
    const {
        setPage,
        currentPage,
        totalPages,
        transactionCount,
        transactions,
        filters,
    } = useContext(PaginatedTransactionsTableContext);

    const { data: statistics } = useTransactionsStatisticsQuery({
        filters,
    });

    const paginationParams = {
        pageCount: totalPages,
        currentPage,
        totalCount: transactionCount,
        onPageChange: setPage,
    };

    const bulkActions = useBulkActions();

    const pageChangeWithBulkActionReset = (newPage: number) => {
        if (bulkActions.selectedCount) {
            bulkActions.clearSelection();
        }
        paginationParams.onPageChange(newPage);
    };

    const passingStatistics = useMemo(() => {
        if (bulkActions.selectedCount === 0 || bulkActions.hasSelectedAll) {
            return statistics;
        }

        const selectedStatistics = (transactions ?? []).reduce(
            (agg, transaction) => {
                if (!bulkActions.isSelected(transaction)) {
                    return agg;
                }

                return {
                    total: agg.total + transaction.amount,
                    cashIn:
                        agg.cashIn +
                        (transaction.amount > 0 ? transaction.amount : 0),
                    cashOut:
                        agg.cashOut +
                        (transaction.amount < 0 ? transaction.amount : 0),
                    averageSum: agg.averageSum + Math.abs(transaction.amount),
                };
            },
            {
                total: 0,
                cashIn: 0,
                cashOut: 0,
                averageSum: 0,
            },
        );
        return {
            ...selectedStatistics,
            average: selectedStatistics.averageSum / bulkActions.selectedCount,
            count: bulkActions.selectedCount,
        };
    }, [bulkActions, statistics, transactions]);

    return (
        <>
            {transactions && transactions.length > 0 && (
                <StickyTableFooter sticky={sticky}>
                    <div className={stickyFooterStyles.row}>
                        <div className={stickyFooterStyles.rowContent}>
                            {passingStatistics && (
                                <TransactionsTotals {...passingStatistics} />
                            )}
                            <TransactionsActionBar />
                            <Pagination
                                {...paginationParams}
                                disabled={
                                    bulkActions.updateDetails?.status ===
                                    BulkUpdateStatus.UPDATING
                                }
                                onPageChange={pageChangeWithBulkActionReset}
                            />
                        </div>
                    </div>
                </StickyTableFooter>
            )}
        </>
    );
};
